import { useMemo } from 'react';

import { CountryEntity, CurrencyType } from 'api/types/entity';
import { DocumentsTable, GridFields } from 'components';
import { useFileUploadField } from 'hooks/useFileUploadField';
import { useTranslation } from 'libs/i18n';
import { FilesUpload, Stack, Typography } from 'libs/ui';
import {
  CountryRiskBadges,
  CountryRiskField,
} from 'modules/client/common/feature';
import {
  Field,
  useFormContext,
} from 'modules/client/common/forms/withdrawalAccountForm';
import { FileExtension } from 'types/file';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  countriesLoading?: boolean;
  countries: CountryEntity[];
}

export const FormContent = ({ countries, countriesLoading }: Props) => {
  const { t } = useTranslation();

  const { values, readOnly, errors } = useFormContext();

  const { onAddFiles, onRemoveFile } = useFileUploadField({
    name: 'ownerConfirmationFiles',
    fileExtensions: [FileExtension.pdf],
  });

  const ownerConfirmationFiles = useMemo(
    () =>
      values.ownerConfirmationFiles.map((file) => ({
        id: file.id ?? uuidv4(),
        ...file,
      })),
    [values.ownerConfirmationFiles]
  );

  return (
    <>
      <GridFields minWidth={320}>
        <Field.Autocomplete
          showRequiredMark
          label={t('client.client.withdrawalAccounts.bankCountry.label')}
          loading={countriesLoading}
          name="bankCountry"
          optionLabelKey="name"
          options={countries}
          optionValueKey="id"
          placeholder={t(
            'client.client.withdrawalAccounts.bankCountry.placeholder'
          )}
          renderOptionRightAdornment={(option) => <CountryRiskBadges
            country={option as CountryEntity}
            field={CountryRiskField.WithdrawalAccountBankCountry}
          />}
          rightStartElement={
            <CountryRiskBadges
              country={values.bankCountry}
              field={CountryRiskField.WithdrawalAccountBankCountry}
            />
          }
        />

        <Field.Autocomplete
          showRequiredMark
          label={t('client.client.withdrawalAccounts.currency.label')}
          loading={countriesLoading}
          name="currency"
          optionLabelKey="label"
          options={Object.values(CurrencyType).map((v) => ({
            value: v,
            label: v,
          }))}
          optionValueKey="value"
          placeholder={t(
            'client.client.withdrawalAccounts.currency.placeholder'
          )}
          variant="select"
        />

        <Field.Text
          showRequiredMark
          label={t('client.client.withdrawalAccounts.swiftCodeLabel.label')}
          name="swiftCode"
          placeholder={t(
            'client.client.withdrawalAccounts.swiftCodeLabel.placeholder'
          )}
        />

        <Field.Text
          showRequiredMark
          label={t('client.client.withdrawalAccounts.bankName.label')}
          name="bankName"
          placeholder={t(
            'client.client.withdrawalAccounts.bankName.placeholder'
          )}
        />

        <Field.Text
          showRequiredMark
          label={t('client.client.withdrawalAccounts.bankAddress.label')}
          name="bankAddress"
          placeholder={t(
            'client.client.withdrawalAccounts.bankAddress.placeholder'
          )}
        />

        <Field.Text
          showRequiredMark
          label={t('client.client.withdrawalAccounts.checkingAccount.label')}
          name="checkingAccount"
          placeholder={t(
            'client.client.withdrawalAccounts.checkingAccount.placeholder'
          )}
        />

        <Field.Text
          label={t(
            'client.client.withdrawalAccounts.correspondentBankSwiftCode.label'
          )}
          name="correspondentBankSwiftCode"
          placeholder={t(
            'client.client.withdrawalAccounts.correspondentBankSwiftCode.placeholder'
          )}
        />

        <Field.Text
          label={t(
            'client.client.withdrawalAccounts.correspondentBankName.label'
          )}
          name="correspondentBankName"
          placeholder={t(
            'client.client.withdrawalAccounts.correspondentBankName.placeholder'
          )}
        />

        <Field.Text
          label={t(
            'client.client.withdrawalAccounts.correspondentBankAccountNumber.label'
          )}
          name="correspondentBankAccountNumber"
          placeholder={t(
            'client.client.withdrawalAccounts.correspondentBankAccountNumber.placeholder'
          )}
        />
      </GridFields>

      {readOnly ? (
        <Stack spacing={12}>
          <Typography as="h3" variant="subtitle">
            {t('components.documentsTableFeature.title')}
          </Typography>
          <DocumentsTable files={ownerConfirmationFiles} />
        </Stack>
      ) : (
        <FilesUpload
          fileExtensions={[FileExtension.pdf]}
          files={ownerConfirmationFiles}
          isError={!!errors.ownerConfirmationFiles}
          name="ownerConfirmationFiles"
          title={t('client.client.withdrawalAccounts.documentTitle')}
          onAddFiles={onAddFiles}
          onRemoveFile={(file) => onRemoveFile(file.id)}
        />
      )}
    </>
  );
};
